// ============================================================================
// CG-CASE-GROUP-001 — Case-Group Master (list + create + detail modal) — Phase A
// ============================================================================
// Enterprise list of case-group events (auction, Adalath, OTS, etc.); opening a
// row launches the left-menu detail modal. Add opens the create modal.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ConfirmDialog, ModuleHeader } from 'components/modals';
import type { ActionButton } from 'components/modals';
import {
  type ActionMenuItem,
  SyutGrid,
  type SyutGridColumn,
  SyutSelect,
} from 'components/ui';
import notificationService from 'services/notificationService';
import { useCaseGroups } from 'hooks/api/useCaseGroups';
import { caseGroupService } from 'api/services/caseGroupService';
import type { CaseGroupListItem, CaseGroupSearchRequest } from 'api/types/caseGroup.types';
import CaseGroupFormModal from 'components/modals/modules/case-group-module/CaseGroupFormModal';
import CaseGroupDetailModal from 'components/modals/modules/case-group-module/CaseGroupDetailModal';

interface CaseGroupMasterScreenProps {
  tabId?: string;
  moduleName?: string;
}

const CaseGroupMasterScreen: React.FC<CaseGroupMasterScreenProps> = () => {
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const [queryParams, setQueryParams] = useState<CaseGroupSearchRequest>({ page: 1, pageSize: 25 });
  const { data, loading, pagination, refetch } = useCaseGroups(queryParams);

  const [searchQuery, setSearchQuery] = useState('');
  const [filterOpen, setFilterOpen] = useState(false);
  const [groupTypeFilter, setGroupTypeFilter] = useState('');
  const [modeFilter, setModeFilter] = useState('');
  const [stateFilter, setStateFilter] = useState('');

  const [createOpen, setCreateOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<CaseGroupListItem | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setQueryParams(p => ({ ...p, searchTerm: searchQuery.trim() || undefined, page: 1 })), 400);
    return () => clearTimeout(t);
  }, [searchQuery]);

  const handlePageChange = useCallback((idx: number) => setQueryParams(p => ({ ...p, page: idx + 1 })), []);
  const handlePageSizeChange = useCallback((size: number) => setQueryParams(p => ({ ...p, pageSize: size, page: 1 })), []);

  const handleApplyFilters = useCallback(() => {
    setQueryParams(p => ({
      ...p,
      groupType: groupTypeFilter || undefined,
      mode: modeFilter || undefined,
      stateCode: stateFilter || undefined,
      page: 1,
    }));
    setFilterOpen(false);
  }, [groupTypeFilter, modeFilter, stateFilter]);

  const handleClearFilters = useCallback(() => {
    setGroupTypeFilter(''); setModeFilter(''); setStateFilter('');
    setQueryParams({ page: 1, pageSize: queryParams.pageSize ?? 25 });
  }, [queryParams.pageSize]);

  const activeFilterCount = useMemo(() => {
    let c = 0;
    if (queryParams.groupType) c++;
    if (queryParams.mode) c++;
    if (queryParams.stateCode) c++;
    return c;
  }, [queryParams]);

  const handleConfirmDelete = useCallback(async () => {
    if (!deleteTarget) return;
    setIsDeleting(true);
    try {
      await caseGroupService.delete(deleteTarget.id);
      setIsDeleting(false);
      setDeleteTarget(null);
      notificationService.toast.success(`Case-group "${deleteTarget.groupCode}" deleted.`);
      refetch();
    } catch (err: any) {
      setIsDeleting(false);
      notificationService.toast.error(err?.response?.data?.message || 'An error occurred while deleting.', 6000);
    }
  }, [deleteTarget, refetch]);

  const fmtDate = (s?: string | null) => { if (!s) return '—'; try { return new Date(s).toLocaleDateString('en-IN'); } catch { return s; } };

  const columns: SyutGridColumn<CaseGroupListItem>[] = useMemo(() => [
    { id: 'groupCode',  header: 'Code',    accessorKey: 'groupCode', size: 180, enableSorting: true },
    { id: 'groupType',  header: 'Type',    accessorKey: 'groupType', size: 130,
      cell: ({ row }) => <span style={{ textTransform: 'capitalize' }}>{row.original.groupType.toLowerCase().replace('_', ' ')}</span>
    },
    { id: 'title',      header: 'Title',   accessorKey: 'title', size: 220, flex: true,
      cell: ({ row }) => row.original.title || <span style={{ color: 'var(--text-muted)' }}>—</span>,
    },
    { id: 'mode',       header: 'Mode',    accessorKey: 'mode', size: 100 },
    { id: 'eventDate',  header: 'Date',    accessorKey: 'eventDate', size: 120, cell: ({ row }) => fmtDate(row.original.eventDate) },
    { id: 'venue',      header: 'Venue',   accessorKey: 'venue', size: 200,
      cell: ({ row }) => row.original.venue || <span style={{ color: 'var(--text-muted)' }}>—</span>,
    },
    { id: 'memberCount', header: 'Members', accessorKey: 'memberCount', size: 90 },
    { id: 'currentStateCode', header: 'Stage', accessorKey: 'currentStateCode', size: 160,
      cell: ({ row }) => (
        <span style={{ fontSize: '0.72rem', fontWeight: 600, color: '#2563eb', background: 'rgba(37,99,235,0.12)', padding: '1px 8px', borderRadius: 999 }}>
          {row.original.currentStateCode || 'DRAFT'}
        </span>
      ),
    },
    { id: 'statusCode', header: 'Status', accessorKey: 'statusCode', size: 100,
      cell: ({ row }) => {
        const code = row.original.deletedAt ? 'DELETED' : (row.original.statusCode || 'ACTIVE');
        const color = code === 'ACTIVE' ? '#16a34a' : code === 'DELETED' ? '#dc2626' : '#6b7280';
        return <span style={{ fontSize: '0.72rem', fontWeight: 600, color, border: `1px solid ${color}`, borderRadius: 999, padding: '1px 8px' }}>{code}</span>;
      },
    },
  ], []);

  const getRowActions = useCallback((r: CaseGroupListItem): ActionMenuItem[] => [
    { id: 'open',   label: 'Open',   icon: <IconSystem.folder size={16} />, onClick: () => setSelectedId(r.id) },
    { id: 'delete', label: 'Delete', icon: <IconSystem.trash size={16} />,  onClick: () => setDeleteTarget(r), variant: 'danger' },
  ], []);

  const actionButtons: ActionButton[] = useMemo(() => [
    { id: 'add',     icon: <IconSystem.add size={18} />,     label: 'Add',     tooltip: 'New case-group', onClick: () => setCreateOpen(true), variant: 'icon' },
    { id: 'refresh', icon: <IconSystem.refresh size={18} />, label: 'Refresh', tooltip: 'Refresh',       onClick: () => refetch(),           variant: 'icon' },
  ], [refetch]);

  return (
    <div className="module-master-container">
      <ModuleHeader
        title="Recovery Bulk Action"
        description="Batch / event proceedings — auction, Adalath, OTS camp, conciliation, settlement drive. Open a group to manage its members, notices, results and settlement."
        icon={<IconSystem.tag size={18} />}
        searchConfig={{ value: searchQuery, onChange: setSearchQuery, placeholder: 'Search code / title / venue…' }}
        filterConfig={{ onClick: () => setFilterOpen(!filterOpen), isActive: filterOpen || activeFilterCount > 0, activeFilterCount }}
        actions={actionButtons}
      />

      <div className="module-content" style={{ marginTop: '6px' }}>
        <div style={{ display: 'flex', gap: '0', flex: 1, overflow: 'hidden' }}>
          <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
            <SyutGrid
              data={data}
              columns={columns}
              enableSorting
              enablePagination
              manualPagination
              totalCount={pagination.totalItems}
              currentPage={(queryParams.page ?? 1) - 1}
              pageSize={queryParams.pageSize ?? 25}
              onPageChange={handlePageChange}
              onPageSizeChange={handlePageSizeChange}
              rowActions={getRowActions}
              isLoading={loading}
              emptyMessage="No case-groups yet"
            />
          </div>
        </div>

        {filterOpen && (
          <div className="search-panel">
            <div className="search-panel-header">
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
                <IconSystem.filter size={14} />
                <h3 className="search-panel-title">Filters</h3>
              </div>
              <button onClick={() => { handleClearFilters(); setFilterOpen(false); }}
                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0.25rem', color: 'var(--text-muted)' }} title="Close filters">
                <IconSystem.x size={16} />
              </button>
            </div>
            <div className="search-panel-content">
              <div className="filter-section">
                <SyutSelect label="Group Type"
                  options={[{ value: '', label: 'All' },
                    { value: 'AUCTION', label: 'Auction' },
                    { value: 'ADALATH', label: 'Adalath' },
                    { value: 'OTS_CAMP', label: 'OTS Camp' },
                    { value: 'CONCILIATION', label: 'Conciliation' },
                    { value: 'SETTLEMENT_DRIVE', label: 'Settlement Drive' },
                    { value: 'LOK_ADALAT', label: 'Lok Adalat' }]}
                  value={groupTypeFilter} onChange={(v) => setGroupTypeFilter((v as string) || '')} isClearable={false} size="sm" />
              </div>
              <div className="filter-section">
                <SyutSelect label="Mode"
                  options={[{ value: '', label: 'All' }, { value: 'PHYSICAL', label: 'Physical' }, { value: 'ONLINE', label: 'Online' }, { value: 'HYBRID', label: 'Hybrid' }]}
                  value={modeFilter} onChange={(v) => setModeFilter((v as string) || '')} isClearable={false} size="sm" />
              </div>
              <div className="filter-section">
                <label className="form-label" style={{ fontSize: '0.8125rem', marginBottom: '0.25rem' }}>Stage</label>
                <input type="text" className="form-input" value={stateFilter} placeholder="e.g. DRAFT, AUCTION_CONDUCTED"
                  onChange={(e) => setStateFilter(e.target.value.toUpperCase())} style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem' }} />
              </div>
            </div>
            <div className="filter-actions">
              <button className="btn btn-secondary" onClick={handleClearFilters}>Clear</button>
              <button className="btn btn-primary" onClick={handleApplyFilters}>Apply</button>
            </div>
          </div>
        )}
      </div>

      <ConfirmDialog
        open={!!deleteTarget}
        title="Delete case-group?"
        message={deleteTarget ? `Delete case-group "${deleteTarget.groupCode}"? Its members are removed too (cascade).` : ''}
        confirmLabel="Delete" cancelLabel="Cancel" variant="danger"
        loading={isDeleting} loadingMessage="Deleting…"
        onConfirm={handleConfirmDelete}
        onCancel={() => { if (!isDeleting) setDeleteTarget(null); }}
      />

      <CaseGroupFormModal isOpen={createOpen} mode="create" onSaved={() => refetch()} onClose={() => setCreateOpen(false)} />

      <CaseGroupDetailModal
        isOpen={!!selectedId}
        groupId={selectedId}
        onClose={() => { setSelectedId(null); refetch(); }}
      />
    </div>
  );
};

export default CaseGroupMasterScreen;
